import { zodResolver } from '@hookform/resolvers/zod';
import { ProgressBar } from '@ntconsult/aluma-react';
import { isAxiosError } from 'axios';
import { useMemo, useRef } from 'react';
import { type FieldErrors, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCidades, useEstados, usePaises, useResponder } from '../hooks';
import type { MinhaParticipacao, RespostaSalvaResultado } from '../types/Formulario';
import {
  BRASIL,
  CAMPO_DA_API,
  CAMPO_DA_API_EXTERIOR,
  caminho,
  camposDoPasso,
  criarSchema,
  instantaneoEntrega,
  montarCorpo,
  oficialDaFotografia,
  type Passo,
  passoAtual,
  passoDoCampo,
  respondido,
  type Valores,
  valoresIniciais,
} from './formulario.utils';
import { PassoBrinde } from './PassoBrinde';
import { PassoCadastro } from './PassoCadastro';
import { PassoConfirmar } from './PassoConfirmar';
import { PassoEntrega } from './PassoEntrega';
import { PassoMesmo } from './PassoMesmo';
import { PassoTamanho } from './PassoTamanho';
import type { CampoEscolha, CtxPasso } from './passo.types';

type Props = {
  participacao: MinhaParticipacao;
  onSalvo: (r: RespostaSalvaResultado) => void;
  /** 409 (versão desatualizada ou situação mudou): a página recarrega e decide pela situação nova. */
  onConflito: (codigo: string) => void;
};

/** Caminhos dos campos com erro (`entrega.cep`, `deseja` …). */
function camposComErro(e: FieldErrors, prefixo = ''): string[] {
  return Object.entries(e).flatMap(([k, x]) =>
    x && typeof x === 'object' && 'type' in x
      ? [`${prefixo}${k}`]
      : x && typeof x === 'object'
        ? camposComErro(x as FieldErrors, `${prefixo}${k}.`)
        : [],
  );
}

/** O formulário do profissional em passos (spec 33): um `useForm` só, um passo por tela. */
export function FormularioCampos({ participacao, onSalvo, onConflito }: Props) {
  const { t } = useTranslation(['formulario', 'erros']);
  const { campanha, dadosApresentados, resposta } = participacao;
  const atual = dadosApresentados?.camisetaAtual ?? null;
  const cadastro = dadosApresentados?.enderecoOficial ?? null;
  const cadastroOk = !!cadastro?.completo;
  const responder = useResponder(campanha.id);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const schema = useMemo(
    () =>
      criarSchema(
        t('formulario:validacao.obrigatorio'),
        t('formulario:validacao.cep'),
        atual,
        cadastroOk,
        {
          telefone: t('formulario:validacao.telefone'),
          tamanho: (max) => t('formulario:validacao.tamanho', { max }),
        },
      ),
    [t, atual, cadastroOk],
  );
  const form = useForm<Valores>({
    resolver: zodResolver(schema) as never,
    defaultValues: valoresIniciais(participacao),
    // R4: o erro de um campo some quando ele é preenchido.
    mode: 'onTouched',
  });
  // A resposta salva com "Sim" no "mesmo" conta como copiada: o oficial salvo fica até a entrega mudar.
  const iniciais = form.formState.defaultValues as Valores;
  const copia = useRef<string | null>(
    iniciais.mesmo === 'sim' ? instantaneoEntrega(iniciais) : null,
  );
  const { getValues, setValue, trigger, setFocus, setError, getFieldState, handleSubmit } = form;
  const v = form.watch();

  const passos = caminho(v);
  const passo = passoAtual(params.get('passo'), v, atual);
  const indice = passos.indexOf(passo);

  // Listas do Axiis: o oficial no Brasil usa estados e cidades; o "mesmo" as carrega ao entrar (R8).
  const marcado = v.oficialMarcado;
  const noMesmo = passo === 'mesmo';
  const entregaExterior = v.deseja === 'sim' && v.origem === 'exterior';
  const paises = usePaises(marcado || entregaExterior || noMesmo);
  const oficialNoBrasil = marcado && v.oficial.paisId === BRASIL;
  const estados = useEstados(oficialNoBrasil || (noMesmo && v.origem !== 'exterior'));
  const cidades = useCidades(
    oficialNoBrasil && v.oficial.estadoId ? Number(v.oficial.estadoId) : null,
  );

  const salvoOficial = resposta?.enderecoOficial.desatualizado ? resposta.enderecoOficial : null;
  const cidadeInicial = salvoOficial?.cidadeId
    ? { id: String(salvoOficial.cidadeId), nome: salvoOficial.cidadeNome ?? '' }
    : !salvoOficial && cadastro?.cidadeId
      ? { id: String(cadastro.cidadeId), nome: cadastro.cidadeNome ?? '' }
      : null;
  const rotulosCidade: Record<string, string> = {};
  if (cidadeInicial) rotulosCidade[cidadeInicial.id] = cidadeInicial.nome;
  for (const c of cidades.data ?? []) rotulosCidade[String(c.id)] = c.nome;

  const irPara = (p: Passo, replace = false) =>
    setParams(
      (atuais) => {
        const novos = new URLSearchParams(atuais);
        novos.set('passo', p);
        return novos;
      },
      { replace },
    );

  function avancar() {
    const ps = caminho(getValues());
    const proximo = ps[ps.indexOf(passo) + 1];
    if (proximo) irPara(proximo);
  }

  function voltar() {
    // R6: o Voltar da tela é o voltar do navegador; aberto direto num passo, vai ao anterior.
    if ((window.history.state?.idx ?? 0) > 0) navigate(-1);
    else irPara(passos[indice - 1] ?? 'brinde', true);
  }

  /** R5: o oficial só fica marcado enquanto o passo que o marcou está no caminho. */
  function aplicarCaminho() {
    const x = getValues();
    const ps = caminho(x);
    if (!ps.includes('mesmo') && x.mesmo) setValue('mesmo', '');
    if (!ps.includes('cadastro') && x.cadastro) setValue('cadastro', '');
    const y = getValues();
    const dono =
      (ps.includes('cadastro') && y.cadastro === 'corrigir') ||
      (ps.includes('mesmo') && y.mesmo === 'sim');
    if (y.oficialMarcado && !dono) {
      setValue('oficialMarcado', false);
      setValue('oficial', oficialDaFotografia(participacao));
      copia.current = null;
    }
  }

  function escolher<K extends CampoEscolha>(campo: K, valor: Valores[K]) {
    setValue(campo, valor as never, { shouldValidate: !!getFieldState(campo).error });
    aplicarCaminho();
  }

  const revalidar = (campo: string) => {
    if (getFieldState(campo as never).error) void trigger(campo as never);
  };

  function focarErro(campos: string[]) {
    const alvo = campos.find((c) => getFieldState(c as never).error);
    // Espera o passo montar antes de focar.
    if (alvo) setTimeout(() => setFocus(alvo as never), 0);
  }

  async function continuar() {
    const campos = camposDoPasso(passo, getValues());
    if (campos.length && !(await trigger(campos as never))) return focarErro(campos);
    avancar();
  }

  function corrigirCadastro() {
    if (getValues('deseja') === 'sim') setValue('corrigirCadastro', true);
    setValue('cadastro', 'corrigir');
    setValue('oficialMarcado', true);
    irPara('cadastro');
  }

  /** R14, R15: vai ao passo do primeiro campo com erro (na ordem do caminho) e foca nele. */
  function irAoErro(campos: string[]) {
    const ps = caminho(getValues());
    const ordem = (c: string) => {
      const i = ps.indexOf(passoDoCampo(c));
      return i < 0 ? ps.length - 1.5 : i;
    };
    const alvo = [...campos].sort((a, b) => ordem(a) - ordem(b))[0];
    if (!alvo) return;
    const destino = passoDoCampo(alvo);
    if (destino === 'cadastro' && passo !== 'cadastro') corrigirCadastro();
    else if (destino !== passo) irPara(destino);
    setTimeout(() => setFocus(alvo as never), 0);
  }

  const semPasso = () =>
    setParams(
      (atuais) => {
        const novos = new URLSearchParams(atuais);
        novos.delete('passo');
        return novos;
      },
      { replace: true },
    );

  async function onValid(valores: Valores) {
    try {
      const r = await responder.mutateAsync(montarCorpo(valores, atual, resposta?.versao ?? null));
      // R6: sem `?passo=`, a página mostra o "Respondido"; o voltar do navegador não cai na confirmação.
      semPasso();
      onSalvo(r);
    } catch (error) {
      if (!isAxiosError(error)) return;
      const status = error.response?.status;
      const codigo = error.response?.data?.codigo as string;
      if (status === 409) {
        semPasso();
        return onConflito(codigo);
      }
      if (status === 422 && codigo === 'RESPOSTA_INVALIDA') {
        // `campos` vem na raiz do corpo de erro, não em `detalhes`.
        const campos: string[] = error.response?.data?.campos ?? [];
        const alvos: string[] = [];
        for (const c of campos) {
          const alvo =
            (getValues('origem') === 'exterior' && CAMPO_DA_API_EXTERIOR[c]) || CAMPO_DA_API[c];
          if (!alvo) continue;
          setError(alvo as never, { message: t('erros:RESPOSTA_INVALIDA') });
          alvos.push(alvo);
        }
        irAoErro(alvos);
      }
    }
  }

  const enviar = handleSubmit(onValid, (e) => irAoErro(camposComErro(e)));

  const ctx: CtxPasso = {
    form,
    participacao,
    atual,
    cadastro,
    respondido: respondido(passo, v, atual),
    onVoltar: indice > 0 ? voltar : undefined,
    avancar,
    continuar,
    escolher,
    corrigirCadastro,
    revalidar,
    copia,
    paises,
    estados,
    cidades,
    rotulosCidade,
    cidadeInicial,
  };

  return (
    <form
      noValidate
      aria-busy={responder.isPending}
      className="flex flex-col gap-4"
      onSubmit={(ev) => {
        // Enter num campo envia o `<form>` (submissão implícita): fora da confirmação, é o Continuar.
        if (passo !== 'confirmar') {
          ev.preventDefault();
          void continuar();
          return;
        }
        void enviar(ev);
      }}
    >
      <div className="flex items-center gap-3 [&>*:last-child]:flex-1">
        <p aria-live="polite" className="shrink-0 text-sm text-foreground-muted">
          {t('formulario:passos.progresso', { atual: indice + 1, total: passos.length })}
        </p>
        <ProgressBar
          value={((indice + 1) / passos.length) * 100}
          label={t('formulario:passos.progresso', { atual: indice + 1, total: passos.length })}
        />
      </div>
      {passo === 'brinde' && <PassoBrinde key={passo} ctx={ctx} />}
      {passo === 'tamanho' && <PassoTamanho key={passo} ctx={ctx} />}
      {passo === 'entrega' && <PassoEntrega key={passo} ctx={ctx} />}
      {passo === 'mesmo' && <PassoMesmo key={passo} ctx={ctx} />}
      {passo === 'cadastro' && <PassoCadastro key={passo} ctx={ctx} />}
      {passo === 'confirmar' && <PassoConfirmar key={passo} ctx={ctx} responder={responder} />}
    </form>
  );
}
